// Guion de la guía de bienvenida (GuiaInteractiva). Recorre las pantallas
// reales resaltando cada zona: en los pasos "observar" se explica la zona y se
// pasa con Siguiente; en los pasos "clic" la persona toca la zona de verdad y
// la guía sigue sola. Las secciones salen solo de los módulos que la persona
// tiene habilitados (mismo criterio que el menú lateral y App.js).
//
// Cada paso es serializable (se guarda en sessionStorage para seguir tras
// navegar o recargar):
// - clave: identificador del paso (lo usa `requiere` de otros pasos)
// - modulo: moduleId del menú que hace falta para verlo (null: siempre)
// - ruta: pantalla del paso (puede llevar ?tab=...); null: la que esté abierta
// - menu: moduleId del enlace del menú que lleva a `ruta` (la guía pide
//   tocarlo; en móvil, antes, el botón del menú)
// - selector: selector CSS (o lista, se usa el primero que esté a la vista);
//   null: tarjeta sin zona resaltada
// - accion: 'observar' (Siguiente) o 'clic' (avanza al tocar la zona)
// - esperar: selector que debe aparecer tras el clic (modal, pestaña)
// - opcional: si la zona no aparece, se salta (o enseña `textoSiFalta`)
// - requiere: clave de un paso anterior; si aquel no encontró su zona, este se salta
// - enMenu: la zona está en el menú lateral (en móvil se abre antes)
// - posicion: 'auto' | 'arriba' | 'abajo' (dónde va la tarjeta)
// - final: último paso (botón Terminar)
import { MENU_ERP } from '../../utils/modulosErp.js';

export const zona = (clave) => `[data-tour="${clave}"]`;

const INICIO = '/dashboard-empleado';
const MENU_INICIO = 'dashboard-empleado';

// Roles del módulo en el menú (también dentro de los grupos), para quien no
// tiene permisos por módulo: mismo criterio que routePermissions de App.js
const rolesDeModulo = (moduleId) => {
  for (const item of MENU_ERP) {
    if (item.moduleId === moduleId) return item.roles || [];
    for (const hijo of item.children || []) {
      if (hijo.moduleId === moduleId) return hijo.roles || item.roles || [];
    }
  }
  return null;
};

export const moduloVisible = (moduleId, permisos, rol) => {
  if (!moduleId) return true;
  if (rol === 'direccion') return true;
  const lista = Array.isArray(permisos) ? permisos : [];
  if (lista.length > 0) return lista.includes(moduleId);
  const roles = rolesDeModulo(moduleId);
  if (!roles) return false;
  return roles.includes('all') || (!!rol && roles.includes(rol));
};

const paso = (datos) => ({
  modulo: null,
  ruta: null,
  menu: null,
  selector: null,
  accion: 'observar',
  esperar: null,
  opcional: false,
  textoSiFalta: null,
  requiere: null,
  enMenu: false,
  posicion: 'auto',
  final: false,
  ...datos,
});

const pasosInicio = () => {
  const base = { ruta: INICIO, menu: MENU_INICIO };
  return [
    paso({
      ...base,
      clave: 'inicio-fichar',
      selector: zona('fichar'),
      titulo: 'Fichar',
      texto: 'Cada día empiezas aquí: este botón ficha tu entrada. Al terminar, el mismo botón ficha la salida.',
    }),
    paso({
      ...base,
      clave: 'inicio-tareas',
      selector: zona('tareas'),
      titulo: 'Tareas y avisos',
      texto: 'Aquí llegan avisos y tareas. Toca Marcar leído o Realizado cuando acabes.',
    }),
    paso({
      ...base,
      clave: 'inicio-semana',
      selector: zona('semana'),
      titulo: 'Tu semana',
      texto: 'Tus eventos de la semana; los tuyos salen destacados.',
    }),
    paso({
      ...base,
      clave: 'inicio-tab-vacaciones',
      selector: zona('tab-vacaciones'),
      accion: 'clic',
      titulo: 'Vacaciones',
      texto: 'Toca Vacaciones.',
    }),
    paso({
      ruta: `${INICIO}?tab=vacaciones`,
      menu: MENU_INICIO,
      clave: 'inicio-vacaciones',
      selector: zona('vacaciones-solicitar'),
      titulo: 'Pedir días',
      texto: 'Desde aquí pides días; verás tu saldo arriba.',
    }),
    paso({
      ...base,
      ruta: `${INICIO}?tab=vacaciones`,
      clave: 'inicio-tab-nominas',
      selector: zona('tab-nominas'),
      accion: 'clic',
      titulo: 'Nóminas',
      texto: 'Toca Nóminas.',
    }),
    paso({
      ruta: `${INICIO}?tab=nominas`,
      menu: MENU_INICIO,
      clave: 'inicio-nominas',
      selector: zona('nominas'),
      opcional: true,
      titulo: 'Tus nóminas',
      texto: 'Cada mes aparece tu nómina aquí.',
    }),
    paso({
      ...base,
      ruta: `${INICIO}?tab=nominas`,
      clave: 'inicio-tab-documentos',
      selector: zona('tab-documentos'),
      accion: 'clic',
      titulo: 'Documentos',
      texto: 'Toca Documentos.',
    }),
    paso({
      ruta: `${INICIO}?tab=documentos`,
      menu: MENU_INICIO,
      clave: 'inicio-firmas',
      selector: [zona('firmas-foto'), zona('para-firmar')],
      opcional: true,
      titulo: 'Firmar documentos',
      texto: 'Cuando te mandemos un papel para firmar, lo descargas, lo firmas y lo subes con una foto desde aquí.',
    }),
    paso({
      ...base,
      ruta: `${INICIO}?tab=documentos`,
      clave: 'inicio-tab-obligaciones',
      selector: zona('tab-obligaciones'),
      accion: 'clic',
      titulo: 'Mis obligaciones',
      texto: 'Toca Obligaciones.',
    }),
    paso({
      ruta: `${INICIO}?tab=obligaciones`,
      menu: MENU_INICIO,
      clave: 'inicio-obligaciones',
      selector: zona('obligaciones-lista'),
      opcional: true,
      titulo: 'Lo que te falta',
      texto: 'Lo que te falta por entregar; cuando lo subas, se pone en Hecho.',
    }),
  ];
};

const pasosFichaje = () => {
  const base = { modulo: 'fichaje', ruta: '/fichaje', menu: 'fichaje' };
  return [
    paso({
      ...base,
      clave: 'fichaje-boton',
      selector: zona('fichaje-boton'),
      titulo: 'Fichaje',
      texto: 'Mismo fichaje que en Mi Inicio, con la pausa de la comida.',
    }),
    paso({
      ...base,
      clave: 'fichaje-resumen',
      selector: zona('fichaje-resumen'),
      opcional: true,
      titulo: 'Tu resumen',
      texto: 'Aquí ves las horas y los días que llevas trabajados esta semana.',
    }),
  ];
};

const pasosCalendario = (esMovil) => {
  const base = { modulo: 'calendario-eventos', ruta: '/calendario-eventos', menu: 'calendario-eventos' };
  const pasos = [];
  if (esMovil) {
    pasos.push(paso({
      ...base,
      clave: 'cal-lista',
      selector: zona('cal-lista'),
      opcional: true,
      titulo: 'Calendario',
      texto: 'Aquí eliges cómo verlo: mes, semana o lista.',
    }));
    // En la vista Semana, tocar un bloque abre el panel con sus eventos
    pasos.push(paso({
      ...base,
      clave: 'cal-bloque',
      selector: zona('cal-bloque'),
      accion: 'clic',
      esperar: zona('cal-evento'),
      opcional: true,
      titulo: 'Tu semana',
      texto: 'Cada bloque es un evento a su hora. Tócalo para ver el detalle.',
    }));
  }
  pasos.push(
    paso({
      ...base,
      clave: 'cal-evento',
      selector: zona('cal-evento'),
      accion: 'clic',
      esperar: zona('evento-cerrar'),
      opcional: true,
      textoSiFalta: 'Este mes no hay eventos a la vista. Cuando los haya, toca uno y verás el horario, el lugar, el contacto, el menú y los PDF.',
      titulo: 'Abre un evento',
      texto: 'Toca un evento para ver todo.',
    }),
    paso({
      ...base,
      clave: 'evento-ir',
      selector: zona('evento-ir'),
      opcional: true,
      requiere: 'cal-evento',
      titulo: 'Ir al evento',
      texto: 'Abre el mapa para llegar.',
    }),
    paso({
      ...base,
      clave: 'evento-telefono',
      selector: zona('evento-telefono'),
      opcional: true,
      requiere: 'cal-evento',
      titulo: 'Contacto',
      texto: 'Toca para llamar al contacto.',
    }),
    paso({
      ...base,
      clave: 'evento-tab-menu',
      selector: zona('evento-tab-menu'),
      accion: 'clic',
      opcional: true,
      requiere: 'cal-evento',
      titulo: 'Menú',
      texto: 'Aquí está el menú del evento. Toca Menú.',
    }),
    paso({
      ...base,
      clave: 'evento-pdf',
      selector: zona('evento-pdf-orden'),
      opcional: true,
      requiere: 'cal-evento',
      titulo: 'PDF del evento',
      texto: 'La orden de cocina y la hoja de material en PDF, siempre actualizadas.',
    }),
    paso({
      ...base,
      clave: 'evento-cerrar',
      selector: zona('evento-cerrar'),
      accion: 'clic',
      requiere: 'cal-evento',
      titulo: 'Cerrar',
      texto: 'Toca la X para cerrar el evento.',
    }),
  );
  return pasos;
};

const pasosCompras = () => {
  const base = { modulo: 'compras', ruta: '/compras/necesidades', menu: 'compras' };
  const deLineas = { opcional: true, requiere: 'compras-proveedor' };
  return [
    paso({
      ...base,
      clave: 'compras-tab-semana',
      selector: zona('compras-tab-semana'),
      titulo: 'Compra de la semana',
      texto: 'Todo lo que hay que pedir esta semana, por proveedor.',
    }),
    paso({
      ...base,
      clave: 'compras-proveedor',
      selector: zona('compras-proveedor'),
      opcional: true,
      textoSiFalta: 'Esta semana no hay nada que pedir. Cuando lo haya, verás aquí cada proveedor con sus líneas para marcarlas como Pedido o Comprado y mandarle el pedido por WhatsApp o en PDF.',
      titulo: 'Cada proveedor',
      texto: 'Cada bloque es un proveedor con lo que hay que pedirle.',
    }),
    paso({
      ...base,
      ...deLineas,
      clave: 'compras-estado',
      selector: zona('compras-estado'),
      titulo: 'Pedido y Comprado',
      texto: 'Cuando pidas una línea, tócala en Pedido; cuando llegue, Comprado. Lo de más queda como reserva.',
    }),
    paso({
      ...base,
      ...deLineas,
      clave: 'compras-todo-pedido',
      selector: zona('compras-todo-pedido'),
      titulo: 'Todo pedido',
      texto: 'Si pides todo lo del proveedor de golpe, márcalo aquí de una vez.',
    }),
    paso({
      ...base,
      ...deLineas,
      clave: 'compras-whatsapp',
      selector: [zona('compras-whatsapp'), zona('compras-pdf')],
      titulo: 'Mandar el pedido',
      texto: 'Le mandas el pedido por WhatsApp o en PDF.',
    }),
    paso({
      ...base,
      ...deLineas,
      clave: 'compras-no-pedir',
      selector: zona('compras-no-pedir'),
      titulo: 'No pedir',
      texto: 'Si ya lo tienes, marca No pedir.',
    }),
  ];
};

const pasosFinales = () => [
  paso({
    clave: 'avisos',
    selector: zona('notificaciones'),
    enMenu: true,
    titulo: 'Avisos',
    texto: 'La campana: aquí avisamos de documentos, cambios y tareas.',
  }),
  paso({
    clave: 'fin',
    ruta: INICIO,
    menu: MENU_INICIO,
    final: true,
    titulo: 'Listo',
    texto: 'Ya sabes dónde está cada cosa. Si no encuentras algo, pregunta a dirección. Puedes repetir la guía desde tu ficha.',
  }),
];

// Pasos de la guía para esta persona: Mi Inicio, Fichaje, Calendario y
// Compras (si tiene esos módulos), avisos y fin.
export const pasosBienvenida = (permisos, rol, esMovil = false) => [
  ...pasosInicio(),
  ...pasosFichaje(),
  ...pasosCalendario(!!esMovil),
  ...pasosCompras(),
  ...pasosFinales(),
].filter((p) => moduloVisible(p.modulo, permisos, rol));

export default pasosBienvenida;
